import { Injectable, OnDestroy, signal } from '@angular/core';
import * as mapboxgl from 'mapbox-gl';
import * as turf from '@turf/turf';
import { Observable } from 'rxjs';
import { AppConfigService } from '../services/app-config.service';
import { IMarker, IMarkerCoordinate, IMarkerOptions } from './models/marker.interface';
import type { Feature, FeatureCollection, MultiPolygon } from 'geojson';
import type { FieldBoundaryCollection } from './services/field-boundary-decoder.service';
import { MapPinLayerService } from './services/map-pin-layer.service';

export {
    PIN_REFERENCE_ZOOM,
    pinIconSizeMapboxExpression,
    pinZoomScaleFactor,
} from './services/map-pin-layer.util';
import {
    buildCropIdColorMatch,
    buildFieldCropFilterParts,
    buildSeasonIdColorMatch,
    type FieldLayerCropStyle,
} from './field-crop-style.util';

export {
    cropForFieldProperties,
    normalizeFieldCropIdKey,
    type FieldLayerCropStyle,
} from './field-crop-style.util';

const FALLBACK_STYLE = 'mapbox://styles/mstfcinar/clfjlt062000101pdmu2dc96j';

/** Served from `public/images/` (matches cropmap-adapted CRM pin cursor). */
export const FIELD_OBSERVATION_PIN_CURSOR = "url('/images/pin-pointer-cursor.svg'), auto";

const CITY_BOUNDARY_SOURCE = 'city-boundary-source';
const CITY_BOUNDARY_LAYER = 'city-boundary-layer';
const DISTRICT_BOUNDARY_SOURCE = 'district-boundary-source';
const DISTRICT_BOUNDARY_LAYER = 'district-boundary-layer';
const FIELD_BOUNDARY_SOURCE = 'field-boundary-source';
const FIELD_BOUNDARY_LAYER = 'field-boundary-layer';

export const CITY_ID_PROPERTY = 'city_id';
export const DISTRICT_ACCESS_KEY_PROPERTY = 'district_access_key';

/** City access keys whose boundaries intersect a map viewport (west, south, east, north). */
export function getCityIdsIntersectingBounds(
    geojson: FeatureCollection<MultiPolygon>,
    west: number,
    south: number,
    east: number,
    north: number,
    allowedCityIds?: readonly string[],
): string[] {
    if (geojson.features.length === 0) {
        return [];
    }

    const viewport = turf.bboxPolygon([west, south, east, north]);
    const allowed =
        allowedCityIds && allowedCityIds.length > 0
            ? new Set(allowedCityIds.map((id) => String(id).trim()).filter(Boolean))
            : null;

    const ids = new Set<string>();
    for (const feature of geojson.features) {
        const raw = feature.properties?.[CITY_ID_PROPERTY];
        if (raw == null) continue;
        const cityId = String(raw).trim();
        if (!cityId) continue;
        if (allowed && !allowed.has(cityId)) continue;

        try {
            if (turf.booleanIntersects(viewport, feature as Feature)) {
                ids.add(cityId);
            }
        } catch {
            /* skip invalid geometry */
        }
    }

    return [...ids].sort();
}
export const DISTRICT_FIELD_FILE_ZOOMS = [9, 10, 11, 12, 13] as const;
export type DistrictFieldFileZoom = (typeof DISTRICT_FIELD_FILE_ZOOMS)[number];
export type FieldFileZoom = 3 | 5 | DistrictFieldFileZoom;
export type FieldUnitType = 'country' | 'district';

export interface FieldTile {
    unitType: FieldUnitType;
    fileZoom: FieldFileZoom;
}

export type SeasonCropProperty = 'summer_crop_id' | 'winter_crop_id';

/** GeoJSON property used for crop id on field features for the active season. */
export function seasonCropPropertyFor(season: { id: string | number; name?: string }): SeasonCropProperty {
    const id = String(season.id);
    if (id === '1' || Number(season.id) === 1) {
        return 'summer_crop_id';
    }
    if (/summer/i.test(season.name ?? '')) {
        return 'summer_crop_id';
    }
    return 'winter_crop_id';
}

/** Nearest district field file zoom for map zoom 9+. */
export function nearestDistrictFileZoom(zoom: number): DistrictFieldFileZoom {
    let nearest: DistrictFieldFileZoom = DISTRICT_FIELD_FILE_ZOOMS[0];
    let minDist = Math.abs(zoom - nearest);
    for (const bucket of DISTRICT_FIELD_FILE_ZOOMS) {
        const dist = Math.abs(zoom - bucket);
        if (dist < minDist) {
            minDist = dist;
            nearest = bucket;
        }
    }
    return nearest;
}

/** Plantation field blob unit type and file zoom from current map zoom (verified blobs only). */
export function fieldTileForZoom(zoom: number): FieldTile {
    if (zoom < 4) {
        return { unitType: 'country', fileZoom: 3 };
    }
    if (zoom < 9) {
        return { unitType: 'country', fileZoom: 5 };
    }
    return { unitType: 'district', fileZoom: nearestDistrictFileZoom(zoom) };
}

export function fieldTilesEqual(a: FieldTile, b: FieldTile): boolean {
    return a.unitType === b.unitType && a.fileZoom === b.fileZoom;
}

@Injectable({ providedIn: 'root' })
export class MapService implements OnDestroy {
    map: mapboxgl.Map | undefined;

    private readonly fieldObservationModeState = signal(false);
    /** Field observation / CRM map interaction mode (drives UI and future map handlers). */
    readonly fieldObservationMode = this.fieldObservationModeState.asReadonly();

    constructor(
        private appConfigService: AppConfigService,
        private mapPinLayer: MapPinLayerService,
    ) {}

    ngOnDestroy(): void {
        this.destroyMap();
    }

    buildMap(container: string | HTMLElement): Observable<mapboxgl.Map> {
        return new Observable((observer) => {
            this.destroyMap();

            setTimeout(() => {
                const style = this.appConfigService.mapboxStyle || FALLBACK_STYLE;
                const token = this.appConfigService.mapboxApiToken;

                this.map = new mapboxgl.Map({
                    container,
                    style,
                    accessToken: token,
                    zoom: 1,
                });

                this.map.on('load', () => {
                    this.mapPinLayer.configure({
                        getMap: () => this.map,
                        getBeforeLabelLayerId: () => this.firstLabelLayerId(),
                    });
                    this.applyFieldObservationModeCursor();
                    observer.next(this.map!);
                    observer.complete();
                });
            }, 100);
        });
    }

    toggleFieldObservationMode(): void {
        const next = !this.fieldObservationModeState();
        this.fieldObservationModeState.set(next);
        this.applyFieldObservationModeCursor();
        if (!next) {
            this.removePinMarker();
        }
    }

    /** Default canvas cursor for field observation / CRM mode (on toggle and when not over a field). */
    applyFieldObservationModeCursor(): void {
        if (!this.map) return;
        this.map.getCanvas().style.cursor = this.fieldObservationModeState()
            ? FIELD_OBSERVATION_PIN_CURSOR
            : '';
    }

    /** Pin cursor when hovering a parcel in CRM mode (field zoom band). */
    setFieldObservationPinCursor(): void {
        if (!this.map) return;
        this.map.getCanvas().style.cursor = FIELD_OBSERVATION_PIN_CURSOR;
    }

    zoom(delta: number): void {
        const currentZoom = this.map?.getZoom();
        if (currentZoom != null) {
            this.map?.zoomTo(currentZoom + delta, { duration: 500 });
        }
    }

    flyTo(center: { lng: number; lat: number }, zoom: number): void {
        this.map?.flyTo({ center: [center.lng, center.lat], essential: true, zoom });
    }

    /**
     * Mapbox `beforeId` for city, district, and field fill layers so place names (e.g. Ankara)
     * render above semi-transparent overlays. Returns undefined when the style has no symbol
     * layers; Mapbox then stacks overlays on top as before.
     */
    private firstLabelLayerId(): string | undefined {
        return this.map?.getStyle()?.layers?.find((layer) => layer.type === 'symbol')?.id;
    }

    // ─── City boundary layer ─────────────────────────────────────────

    private cityBoundaryGeoJson: FeatureCollection<MultiPolygon> | null = null;
    private authorizedCityMatchFilter: mapboxgl.FilterSpecification | null = null;
    private authorizedCityOpacityExpr: mapboxgl.ExpressionSpecification | null = null;

    setCityBoundaryData(geojson: FeatureCollection<MultiPolygon>): void {
        this.removeCityBoundaryLayers();
        this.cityBoundaryGeoJson = geojson;

        if (!this.map || geojson.features.length === 0) return;

        this.map.addSource(CITY_BOUNDARY_SOURCE, {
            type: 'geojson',
            data: geojson,
        });

        this.map.addLayer(
            {
                id: CITY_BOUNDARY_LAYER,
                type: 'fill',
                source: CITY_BOUNDARY_SOURCE,
                paint: {
                    'fill-outline-color': '#FFFFFF',
                    'fill-color': '#000000',
                    'fill-opacity': 0.6,
                },
                filter: ['!=', ['get', CITY_ID_PROPERTY], ''],
            },
            this.firstLabelLayerId(),
        );
    }

    removeCityBoundaryLayers(): void {
        if (!this.map) return;
        if (this.map.getLayer(CITY_BOUNDARY_LAYER)) {
            this.map.removeLayer(CITY_BOUNDARY_LAYER);
        }
        if (this.map.getSource(CITY_BOUNDARY_SOURCE)) {
            this.map.removeSource(CITY_BOUNDARY_SOURCE);
        }
        this.cityBoundaryGeoJson = null;
        this.authorizedCityMatchFilter = null;
        this.authorizedCityOpacityExpr = null;
    }

    /**
     * Dims unauthorized cities to opacity 0 and keeps authorized ones at 0.6.
     * Pass an empty array to show all cities.
     */
    applyCityAuthorizationFilter(authorizedCityIds: string[]): void {
        if (!this.map || !this.map.getLayer(CITY_BOUNDARY_LAYER)) return;

        if (authorizedCityIds.length === 0) return;

        const matchFilter: mapboxgl.FilterSpecification = [
            'any',
            ...authorizedCityIds.map(
                (id) => ['==', ['get', CITY_ID_PROPERTY], id] as mapboxgl.ExpressionSpecification,
            ),
        ];

        this.authorizedCityMatchFilter = matchFilter;
        const opacityExpr: mapboxgl.ExpressionSpecification = ['case', matchFilter, 0.6, 0];
        this.authorizedCityOpacityExpr = opacityExpr;

        this.map.setFilter(CITY_BOUNDARY_LAYER, matchFilter);
        this.map.setPaintProperty(CITY_BOUNDARY_LAYER, 'fill-opacity', opacityExpr);
    }

    /** Hides a single city polygon (e.g. after clicking it to drill into districts). */
    hideCityById(cityId: string): void {
        if (!this.map || !this.map.getLayer(CITY_BOUNDARY_LAYER)) return;

        const hideExpr: mapboxgl.ExpressionSpecification = [
            '!=',
            ['get', CITY_ID_PROPERTY],
            cityId,
        ];

        if (this.authorizedCityMatchFilter) {
            const combined: mapboxgl.FilterSpecification = [
                'all',
                this.authorizedCityMatchFilter,
                hideExpr,
            ];
            this.map.setFilter(CITY_BOUNDARY_LAYER, combined);
        } else {
            this.map.setFilter(CITY_BOUNDARY_LAYER, hideExpr);
        }
    }

    /** Sets hover highlight: the hovered city gets lower opacity. */
    setCityHoverOpacity(hoveredCityId: string): void {
        if (!this.map || !this.map.getLayer(CITY_BOUNDARY_LAYER)) return;
        this.map.setPaintProperty(CITY_BOUNDARY_LAYER, 'fill-opacity', [
            'match',
            ['get', CITY_ID_PROPERTY],
            hoveredCityId,
            0.2,
            0.6,
        ]);
    }

    /** Resets city layer to default uniform opacity. */
    resetCityOpacity(): void {
        if (!this.map || !this.map.getLayer(CITY_BOUNDARY_LAYER)) return;
        if (this.authorizedCityOpacityExpr) {
            this.map.setPaintProperty(
                CITY_BOUNDARY_LAYER,
                'fill-opacity',
                this.authorizedCityOpacityExpr,
            );
        } else {
            this.map.setPaintProperty(CITY_BOUNDARY_LAYER, 'fill-opacity', 0.6);
        }
    }

    /** City ids from loaded city boundaries that intersect the current map viewport. */
    getCityIdsInMapViewport(allowedCityIds?: readonly string[]): string[] {
        if (!this.map || !this.cityBoundaryGeoJson) {
            return [];
        }
        const bounds = this.map.getBounds();
        if (!bounds) {
            return [];
        }
        return getCityIdsIntersectingBounds(
            this.cityBoundaryGeoJson,
            bounds.getWest(),
            bounds.getSouth(),
            bounds.getEast(),
            bounds.getNorth(),
            allowedCityIds,
        );
    }

    /** Zooms to the bounding box of a city feature by its city_id. */
    zoomToCityBounds(cityId: string): void {
        if (!this.map || !this.cityBoundaryGeoJson) return;

        const feature = this.cityBoundaryGeoJson.features.find(
            (f) => f.properties?.[CITY_ID_PROPERTY] === cityId,
        );
        if (!feature) {
            console.warn(`[MapService] City ${cityId} not found in boundary data.`);
            return;
        }

        const bbox = turf.bbox(feature) as [number, number, number, number];
        this.map.resize();
        this.map.fitBounds(bbox, {
            padding: 40,
            duration: 2000,
            minZoom: 9,
        });
    }

    /** Zooms to fit all city boundary features (i.e. the whole country). */
    zoomToAllCityBounds(): void {
        if (!this.map || !this.cityBoundaryGeoJson || this.cityBoundaryGeoJson.features.length === 0) {
            console.log('[MapService] zoomToAllCityBounds: no data');
            return;
        }

        const bbox = turf.bbox(this.cityBoundaryGeoJson) as [number, number, number, number];
        console.log('[MapService] zoomToAllCityBounds bbox:', bbox);
        this.map.resize();
        this.map.fitBounds(bbox, {
            padding: 40,
            duration: 1500,
        });
    }

    getCityBoundaryLayerId(): string {
        return CITY_BOUNDARY_LAYER;
    }

    // ─── District boundary layer ─────────────────────────────────────

    private districtBoundaryGeoJson: FeatureCollection<MultiPolygon> | null = null;

    setDistrictBoundaryData(geojson: FeatureCollection<MultiPolygon>): void {
        this.removeDistrictBoundaryLayers();
        this.districtBoundaryGeoJson = geojson;

        if (!this.map || geojson.features.length === 0) return;

        this.map.addSource(DISTRICT_BOUNDARY_SOURCE, {
            type: 'geojson',
            data: geojson,
        });

        this.map.addLayer(
            {
                id: DISTRICT_BOUNDARY_LAYER,
                type: 'fill',
                source: DISTRICT_BOUNDARY_SOURCE,
                paint: {
                    'fill-outline-color': '#d3a453',
                    'fill-color': '#000000',
                    'fill-opacity': 0.4,
                },
                filter: ['!=', ['get', DISTRICT_ACCESS_KEY_PROPERTY], ''],
            },
            this.firstLabelLayerId(),
        );

        this.ensureDistrictBoundaryAboveFieldLayer();
    }

    removeDistrictBoundaryLayers(): void {
        if (!this.map) return;
        if (this.map.getLayer(DISTRICT_BOUNDARY_LAYER)) {
            this.map.removeLayer(DISTRICT_BOUNDARY_LAYER);
        }
        if (this.map.getSource(DISTRICT_BOUNDARY_SOURCE)) {
            this.map.removeSource(DISTRICT_BOUNDARY_SOURCE);
        }
        this.districtBoundaryGeoJson = null;
    }

    applyDistrictAuthorizationFilter(authorizedDistrictAccessKeys: string[]): void {
        if (!this.map?.getLayer(DISTRICT_BOUNDARY_LAYER)) return;
        if (authorizedDistrictAccessKeys.length === 0) return;

        const matchFilter: mapboxgl.FilterSpecification = [
            'any',
            ...authorizedDistrictAccessKeys.map(
                (id) =>
                    ['==', ['get', DISTRICT_ACCESS_KEY_PROPERTY], id] as mapboxgl.ExpressionSpecification,
            ),
        ];

        this.map.setFilter(DISTRICT_BOUNDARY_LAYER, matchFilter);

        const opacityExpr: mapboxgl.ExpressionSpecification = ['case', matchFilter, 0.4, 0];
        this.map.setPaintProperty(DISTRICT_BOUNDARY_LAYER, 'fill-opacity', opacityExpr);
    }

    setDistrictHoverOpacity(hoveredDistrictAccessKey: string): void {
        if (!this.map?.getLayer(DISTRICT_BOUNDARY_LAYER)) return;
        this.map.setPaintProperty(DISTRICT_BOUNDARY_LAYER, 'fill-opacity', [
            'match',
            ['get', DISTRICT_ACCESS_KEY_PROPERTY],
            hoveredDistrictAccessKey,
            0.15,
            0.4,
        ]);
    }

    resetDistrictOpacity(): void {
        if (!this.map?.getLayer(DISTRICT_BOUNDARY_LAYER)) return;
        this.map.setPaintProperty(DISTRICT_BOUNDARY_LAYER, 'fill-opacity', 0.4);
    }

    getDistrictBoundaryLayerId(): string {
        return DISTRICT_BOUNDARY_LAYER;
    }

    hasDistrictBoundaryLayer(): boolean {
        return !!this.map?.getLayer(DISTRICT_BOUNDARY_LAYER);
    }

    // ─── Field boundary layer (crop plantation polygons) ─────────────

    private fieldBoundaryGeoJson: FieldBoundaryCollection | null = null;

    /**
     * Draw or update plantation field polygons. Prefer in-place GeoJSON updates so zoom
     * tile reloads do not remove the layer (avoids a blank map while blobs load).
     */
    setFieldBoundaryData(geojson: FieldBoundaryCollection): void {
        if (!this.map) {
            // Map not ready yet; cache for callers that check fieldBoundaryGeoJson.
            this.fieldBoundaryGeoJson = geojson.features.length > 0 ? geojson : null;
            return;
        }

        if (geojson.features.length === 0) {
            this.removeFieldBoundaryLayers();
            return;
        }

        this.fieldBoundaryGeoJson = geojson;

        const existingSource = this.map.getSource(FIELD_BOUNDARY_SOURCE);
        if (existingSource && 'setData' in existingSource) {
            // Same source/layer: swap features (e.g. country z5 → district z10 after fetch).
            (existingSource as mapboxgl.GeoJSONSource).setData(geojson);
            this.ensureDistrictBoundaryAboveFieldLayer();
            this.mapPinLayer.ensureLayersAboveFields();
            return;
        }

        // First draw or source was torn down: create source + fill layer.
        this.removeFieldBoundaryLayers();
        this.fieldBoundaryGeoJson = geojson;

        this.map.addSource(FIELD_BOUNDARY_SOURCE, {
            type: 'geojson',
            data: geojson,
        });

        this.map.addLayer(
            {
                id: FIELD_BOUNDARY_LAYER,
                type: 'fill',
                source: FIELD_BOUNDARY_SOURCE,
                paint: {
                    'fill-outline-color': '#d3a453',
                    'fill-color': '#000000',
                    'fill-opacity': 0.8,
                },
            },
            this.firstLabelLayerId(),
        );

        this.ensureDistrictBoundaryAboveFieldLayer();
        this.mapPinLayer.ensureLayersAboveFields();
    }

    /**
     * Keeps district fills above field polygons (click/hover target) but still below basemap
     * labels via `firstLabelLayerId()`.
     */
    private ensureDistrictBoundaryAboveFieldLayer(): void {
        if (!this.map?.getLayer(DISTRICT_BOUNDARY_LAYER)) {
            return;
        }
        this.map.moveLayer(DISTRICT_BOUNDARY_LAYER, this.firstLabelLayerId());
        this.mapPinLayer.ensureLayersAboveFields();
    }

    removeFieldBoundaryLayers(): void {
        if (!this.map) return;
        if (this.map.getLayer(FIELD_BOUNDARY_LAYER)) {
            this.map.removeLayer(FIELD_BOUNDARY_LAYER);
        }
        if (this.map.getSource(FIELD_BOUNDARY_SOURCE)) {
            this.map.removeSource(FIELD_BOUNDARY_SOURCE);
        }
        this.fieldBoundaryGeoJson = null;
    }

    /** Colors field polygons by crop_id (GeographicUnitPayload) or season crop id (legacy). */
    setFieldColorBySeason(crops: FieldLayerCropStyle[], seasonCropProperty: SeasonCropProperty): void {
        if (!this.map?.getLayer(FIELD_BOUNDARY_LAYER) || crops.length === 0) return;

        const byCropId = buildCropIdColorMatch(crops);
        const bySeasonId = buildSeasonIdColorMatch(crops, seasonCropProperty);

        let fillColor: mapboxgl.ExpressionSpecification | string;

        if (byCropId && bySeasonId) {
            fillColor = ['case', ['has', 'crop_id'], byCropId, bySeasonId];
        } else if (byCropId) {
            fillColor = byCropId;
        } else if (bySeasonId) {
            fillColor = bySeasonId;
        } else {
            fillColor = crops.find((c) => c.color)?.color ?? '#FDFDFD';
        }

        this.map.setPaintProperty(FIELD_BOUNDARY_LAYER, 'fill-color', fillColor);
    }

    /** Filters visible fields by crop, optional field size range, and authorized districts. */
    filterFields(
        crops: FieldLayerCropStyle[],
        seasonCropProperty: SeasonCropProperty,
        minFieldSize = 0,
        maxFieldSize = 0,
        districtFilter: (number | string)[] = [],
    ): void {
        if (!this.map?.getLayer(FIELD_BOUNDARY_LAYER)) return;

        const seasonParts = buildFieldCropFilterParts(crops, seasonCropProperty);
        if (seasonParts.length === 0) return;

        const combinedSeason: mapboxgl.FilterSpecification = ['any', ...seasonParts];
        let filter: mapboxgl.FilterSpecification = ['all', combinedSeason];

        if (minFieldSize > 0 || maxFieldSize > 0) {
            const sizeParts: mapboxgl.FilterSpecification[] = [filter];
            if (minFieldSize > 0) {
                sizeParts.push(['>=', ['get', 'field_area'], minFieldSize]);
            }
            if (maxFieldSize > 0) {
                sizeParts.push(['<=', ['get', 'field_area'], maxFieldSize]);
            }
            filter = ['all', ...sizeParts] as mapboxgl.FilterSpecification;
        }

        if (districtFilter.length > 0) {
            const districtParts: mapboxgl.ExpressionSpecification[] = districtFilter.map((id) =>
                typeof id === 'string'
                    ? (['==', ['get', 'district_id'], id] as mapboxgl.ExpressionSpecification)
                    : ([
                          '==',
                          ['to-number', ['get', 'district_id']],
                          id,
                      ] as mapboxgl.ExpressionSpecification),
            );
            filter = ['all', filter, ['any', ...districtParts]] as mapboxgl.FilterSpecification;
        }

        this.map.setFilter(FIELD_BOUNDARY_LAYER, filter);
    }

    getFieldBoundaryLayerId(): string {
        return FIELD_BOUNDARY_LAYER;
    }

    hasFieldBoundaryLayer(): boolean {
        return !!this.map?.getLayer(FIELD_BOUNDARY_LAYER);
    }

    fieldTileForCurrentZoom(): FieldTile {
        return fieldTileForZoom(this.map?.getZoom() ?? 9);
    }

    // ─── Generic helpers ────────────────────────────────────────────

    /** Call when the map container size changes (e.g. side panel open/close). */
    resizeMap(): void {
        try {
            this.map?.resize();
        } catch {
            /* map may be tearing down */
        }
    }

    /** @see MapPinLayerService.addPinMarker */
    addPinMarker(coordinate: IMarkerCoordinate, options?: IMarkerOptions): mapboxgl.Marker {
        return this.mapPinLayer.addPinMarker(coordinate, options);
    }

    /** @see MapPinLayerService.removePinMarker */
    removePinMarker(): void {
        this.mapPinLayer.removePinMarker();
    }

    /** @see MapPinLayerService.addMultipleMarkers */
    addMultipleMarkers(markersData: IMarker[]): void {
        this.mapPinLayer.addMultipleMarkers(markersData);
    }

    /** @see MapPinLayerService.removeBatchMarkers */
    removeBatchMarkers(): void {
        this.mapPinLayer.removeBatchMarkers();
    }

    removeAllMarkers(): void {
        this.mapPinLayer.removeAllMarkers();
    }

    destroyMap(): void {
        this.mapPinLayer.removeAllMarkers();
        this.removeFieldBoundaryLayers();
        this.removeDistrictBoundaryLayers();
        this.removeCityBoundaryLayers();
        if (this.map) {
            this.map.remove();
            this.map = undefined;
        }
        this.mapPinLayer.reset();
    }

    /** Tear down map and CRM mode on logout / account switch. */
    resetForLogout(): void {
        this.fieldObservationModeState.set(false);
        this.destroyMap();
    }
}
